import Phaser from "phaser";
import { ScoreManager } from "@/phaser/core/game/ScoreManager";
import type { ObstacleConfig } from "./obstaclesCatalog";
import { AudioManager } from "../audio/AudioManager";
import { t } from "@/i18n/i18n";

interface ObstacleFailureHandlerDeps {
  scene: Phaser.Scene;
  scoreManager: ScoreManager;
}

interface ObstacleFailureResult {
  scorePenalty: number;
}

// 패널티 효과 지속시간
const OBSTACLE_PENALTY_DURATION = 10000;

// 방해물 실패 처리 담당 클래스
export class ObstacleFailureHandler {
  private scene: Phaser.Scene;
  private scoreManager: ScoreManager;

  private topWarningTimer?: Phaser.Time.TimerEvent;

  private seaObstacleParticles?: Phaser.GameObjects.Particles.ParticleEmitter;
  private seaObstacleEndTimer?: Phaser.Time.TimerEvent;
  private seaObstacleDestroyTimer?: Phaser.Time.TimerEvent;

  private desertSmokeTimer?: Phaser.Time.TimerEvent;
  private desertObstacleEndTimer?: Phaser.Time.TimerEvent;

  private forestObstacleEndTimer?: Phaser.Time.TimerEvent;

  private factoryObstacleEndTimer?: Phaser.Time.TimerEvent;

  constructor(deps: ObstacleFailureHandlerDeps) {
    this.scene = deps.scene;
    this.scoreManager = deps.scoreManager;
  }

  handle(obstacle: ObstacleConfig): ObstacleFailureResult {
    const scorePenalty = this.applyCommonScorePenalty();
    this.showTopWarningText(
      `⚠ ${t(`obstacle.${obstacle.id}.name`)} ${t("game.obstacle_Incoming")} ⚠`,
    );

    switch (obstacle.id) {
      case "obstacle-001":
        this.handleSeaObstacleFailure();
        break;
      case "obstacle-002":
        this.handleDesertObstacleFailure();
        break;
      case "obstacle-003":
        this.handleForestObstacleFailure();
        break;
      case "obstacle-004":
        this.handleFactoryObstacleFailure();
        break;
      default:
        break;
    }

    return { scorePenalty };
  }

  // 얼음 바다 방해물 실패 처리
  private handleSeaObstacleFailure() {
    const { width } = this.scene.scale;

    AudioManager.playSfx("sfx-sea-obstacle");
    this.scene.cameras.main.shake(250, 0.01);

    const particles = this.scene.add.particles(0, 0, "001-sea-obstacle-fail", {
      frame: [0, 1],
      x: { min: 0, max: width + 500 },
      y: -256,
      lifespan: { min: 1000, max: 1800 },
      speedX: { min: -900, max: -200 },
      speedY: { min: 900, max: 1800 },
      quantity: 8,
      frequency: 100,
      scale: { min: 0.2, max: 1 },
      alpha: { min: 0.8, max: 1 },
      angle: { min: 0, max: 30 },
      rotate: { start: 0, end: 90 },
      blendMode: Phaser.BlendModes.NORMAL,
    });

    particles.setDepth(300);
    particles.setScrollFactor(0);

    this.seaObstacleParticles = particles;

    this.seaObstacleEndTimer = this.scene.time.delayedCall(
      OBSTACLE_PENALTY_DURATION,
      () => {
        particles.stop();

        this.seaObstacleDestroyTimer = this.scene.time.delayedCall(1800, () => {
          particles.destroy();

          if (this.seaObstacleParticles === particles) {
            this.seaObstacleParticles = undefined;
            this.seaObstacleEndTimer = undefined;
            this.seaObstacleDestroyTimer = undefined;
          }
        });
      },
    );
  }

  // 사막 방해물 실패 처리
  private handleDesertObstacleFailure() {
    const { width, height } = this.scene.scale;

    AudioManager.playSfx("sfx-desert-obstacle");
    this.scene.cameras.main.shake(250, 0.01);

    const textureKey = "002-desert-obstacle-fail";
    const smokeGroup = this.scene.add.group();

    const radius = 200;

    const path = new Phaser.Curves.Path(width / 2 + radius, height / 2);

    path.circleTo(radius);

    const eyeFx = this.scene.add
      .follower(
        path,
        width / 2 + radius,
        height / 2,
        "002-desert-obstacle-fail-2",
      )
      .setOrigin(0.5)
      .setDepth(299);

    eyeFx.startFollow({
      duration: 5000,
      repeat: -1,
      rotateToPath: false,
    });

    const createSmokeAnim = (key: string, start: number, end: number) => {
      if (this.scene.anims.exists(key)) return;

      this.scene.anims.create({
        key,
        frames: this.scene.anims.generateFrameNumbers(textureKey, {
          start,
          end,
        }),
        frameRate: 10,
        repeat: -1,
      });
    };

    createSmokeAnim("desert-curse-smoke-1", 0, 3);
    createSmokeAnim("desert-curse-smoke-2", 5, 7);

    const spawnSmoke = () => {
      const scale = Phaser.Math.FloatBetween(1, 2);
      const ratio = scale - 1;

      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(height * 0.15, height * 0.95);
      const moveY = Phaser.Math.Linear(130, 320, ratio);
      const moveXRange = Phaser.Math.Linear(20, 60, ratio);
      const moveX = Phaser.Math.Between(-moveXRange, moveXRange);
      const duration = Phaser.Math.Linear(500, 1000, ratio);

      const smoke = this.scene.add
        .sprite(x, y, textureKey)
        .setDepth(300)
        .setScrollFactor(0)
        .setAlpha(0)
        .setScale(scale);

      smoke.play(
        Phaser.Math.Between(0, 1) === 0
          ? "desert-curse-smoke-1"
          : "desert-curse-smoke-2",
      );

      smokeGroup.add(smoke);

      this.scene.tweens.add({
        targets: smoke,
        y: y - moveY,
        x: x + moveX,
        scale: scale * 1.5,
        duration: duration * 2,
        ease: "Sine.out",
      });

      this.scene.tweens.add({
        targets: smoke,
        alpha: { from: 0, to: 0.9, yoyo: true },
        duration,
        ease: "Sine.out",
        onComplete: () => smokeGroup.remove(smoke, true, true),
      });
    };

    this.desertSmokeTimer?.remove(false);
    this.desertObstacleEndTimer?.remove(false);

    this.desertSmokeTimer = this.scene.time.addEvent({
      delay: 200,
      loop: true,
      callback: spawnSmoke,
    });

    this.desertObstacleEndTimer = this.scene.time.delayedCall(
      OBSTACLE_PENALTY_DURATION,
      () => {
        this.desertSmokeTimer?.remove(false);
        this.desertSmokeTimer = undefined;

        eyeFx.stopFollow();

        this.scene.tweens.add({
          targets: eyeFx,
          alpha: 0,
          duration: 1000,
          ease: "Sine.out",
          onComplete: () => eyeFx.destroy(),
        });

        this.desertObstacleEndTimer = undefined;
      },
    );
  }

  // 숲 방해물 실패 처리
  private handleForestObstacleFailure() {
    const { width, height } = this.scene.scale;

    const VINE_TEXTURE_KEY = "003-forest-obstacle-fail";

    AudioManager.playSfx("sfx-forest-obstacle");

    this.scene.cameras.main.shake(250, 0.01);

    const vines: Phaser.GameObjects.Image[] = [];

    const spawnVines = (side: number, count: number) => {
      for (let i = 0; i < count; i++) {
        const frame = Phaser.Math.Between(0, 6);

        let startX = 0;
        let startY = 0;
        let targetX = 0;
        let targetY = 0;

        const OUT_DISTANCE = 1024;
        const ANGLE_VARIATION = 1548;

        switch (side) {
          case 0:
            // 왼쪽
            targetX = 0;
            targetY = ((i + 1) / (count + 1)) * height;

            startX = -OUT_DISTANCE;
            startY =
              targetY + Phaser.Math.Between(-ANGLE_VARIATION, ANGLE_VARIATION);
            break;

          case 1:
            // 오른쪽
            targetX = width;
            targetY = ((i + 1) / (count + 1)) * height;

            startX = width + OUT_DISTANCE;
            startY =
              targetY + Phaser.Math.Between(-ANGLE_VARIATION, ANGLE_VARIATION);
            break;

          case 2:
            // 위
            targetX = ((i + 1) / (count + 1)) * width;
            targetY = 0;

            startX =
              targetX + Phaser.Math.Between(-ANGLE_VARIATION, ANGLE_VARIATION);
            startY = -OUT_DISTANCE;
            break;

          case 3:
            // 아래
            targetX = ((i + 1) / (count + 1)) * width;
            targetY = height;

            startX =
              targetX + Phaser.Math.Between(-ANGLE_VARIATION, ANGLE_VARIATION);
            startY = height + OUT_DISTANCE;
            break;
        }

        const angle = Phaser.Math.Angle.Between(
          startX,
          startY,
          targetX,
          targetY,
        );

        const BACK_OFFSET = 120;

        targetX -= Math.cos(angle) * BACK_OFFSET;
        targetY -= Math.sin(angle) * BACK_OFFSET;

        const vine = this.scene.add
          .image(startX, startY, VINE_TEXTURE_KEY, frame)
          .setOrigin(0, 0.5)
          .setRotation(angle)
          .setDepth(300)
          .setScrollFactor(0);

        vines.push(vine);

        // 천천히 흔들리는 효과
        this.scene.tweens.add({
          targets: vine,
          rotation: angle + Phaser.Math.FloatBetween(-0.1, 0.1),
          duration: Phaser.Math.Between(900, 1400),
          yoyo: true,
          repeat: -1,
          ease: "Sine.inOut",
        });

        this.scene.tweens.add({
          targets: vine,
          x: targetX,
          y: targetY,
          duration: 2000,
        });
      }
    };

    spawnVines(0, 4);
    spawnVines(1, 4);
    spawnVines(2, 3);
    spawnVines(3, 3);

    this.forestObstacleEndTimer = this.scene.time.delayedCall(
      OBSTACLE_PENALTY_DURATION,
      () => {
        vines.forEach((vine) => {
          if (!vine.active) return;

          this.scene.tweens.add({
            targets: vine,
            alpha: 0,
            duration: 700,
            ease: "Cubic.out",
            onComplete: () => {
              vine.destroy();
            },
          });

          this.forestObstacleEndTimer = undefined;
        });
      },
    );
  }

  // 공장 방해물 실패 처리
  private handleFactoryObstacleFailure() {
    const { width, height } = this.scene.scale;

    AudioManager.playSfx("sfx-factory-obstacle");

    this.scene.cameras.main.shake(250, 0.01);

    // 배경 이펙트
    const overlay = this.scene.add
      .image(width / 2, height / 2, "004-factory-obstacle-fail-2")
      .setDepth(300)
      .setScrollFactor(0)
      .setAlpha(0);

    overlay.setScale(Math.max(width / overlay.width, height / overlay.height));

    this.scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 150,
      ease: "Quad.out",
    });

    // 경고띠들
    const tapes: Phaser.GameObjects.Image[] = [];

    const positions = [
      { x: 0.5, y: 0.2 },
      { x: 0.5, y: 0.38 },
      { x: 0.5, y: 0.54 },
      { x: 0.5, y: 0.7 },
      { x: 0.5, y: 0.85 },
    ];

    positions.forEach((pos, index) => {
      this.scene.time.delayedCall(index * 90, () => {
        const tape = this.scene.add
          .image(
            width * pos.x + Phaser.Math.Between(-30, 30),
            height * pos.y,
            "004-factory-obstacle-fail",
          )
          .setDepth(450)
          .setScrollFactor(0)

          .setAngle(Phaser.Math.Between(-30, 30))
          .setScale(3);

        tapes.push(tape);

        // 스티커 붙는 느낌
        this.scene.tweens.add({
          targets: tape,

          scaleX: 1.1,
          scaleY: 1.1,
          duration: 140,
          ease: "Back.out",
        });

        // 붙을 때 살짝 충격감
        this.scene.cameras.main.shake(60, 0.002);
      });
    });

    // 종료
    this.factoryObstacleEndTimer = this.scene.time.delayedCall(
      OBSTACLE_PENALTY_DURATION,
      () => {
        // 배경 먼저 페이드
        this.scene.tweens.add({
          targets: overlay,
          alpha: 0,
          duration: 500,
          ease: "Cubic.out",
        });

        // 테이프 떨어뜨리기
        tapes.forEach((tape, index) => {
          this.scene.tweens.add({
            targets: tape,
            y: tape.y + Phaser.Math.Between(180, 260),
            x: tape.x + Phaser.Math.Between(-40, 40),
            angle: tape.angle + Phaser.Math.Between(-25, 25),
            // alpha: 0,
            scaleY: 0.85,
            duration: 700,
            delay: index * 60,
            ease: "Cubic.in",
            onComplete: () => {
              tape.destroy();
            },
          });
        });

        // overlay 제거
        this.scene.time.delayedCall(800, () => {
          overlay.destroy();
        });

        this.factoryObstacleEndTimer = undefined;
      },
    );
  }

  // 공통 점수 패널티
  private applyCommonScorePenalty(): number {
    const currentScore = this.scoreManager.getScore();

    if (currentScore < 10) {
      return 0;
    }

    const penalty = Math.floor(currentScore * 0.1);

    if (penalty <= 0) {
      return 0;
    }

    this.scoreManager.subtract(penalty);

    return penalty;
  }

  // 방해물 경고 텍스트
  private showTopWarningText(
    message: string,
    duration = OBSTACLE_PENALTY_DURATION,
  ) {
    const { width } = this.scene.scale;
    const baseX = width / 2;
    const baseY = 90;

    const text = this.scene.add
      .text(baseX, baseY, message, {
        fontFamily: "Black Han Sans",
        fontSize: "64px",
        color: "#1C120E",
        stroke: "#FFD93D",
        strokeThickness: 8,
        align: "center",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1000)
      .setAlpha(0)
      .setScale(0.7);

    // 1) 등장: 제자리에서 퍽 + 흔들림
    this.scene.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 100,
      ease: "Back.Out",
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          x: { from: baseX - 10, to: baseX + 10 },
          duration: 40,
          ease: "Sine.InOut",
          yoyo: true,
          repeat: 2,
          onComplete: () => {
            text.setX(baseX);

            // 2) 유지: 패널티 지속 시간 동안 깜빡임
            const blinkTween = this.scene.tweens.add({
              targets: text,
              alpha: { from: 1, to: 0.35 },
              duration: 500,
              ease: "Sine.InOut",
              yoyo: true,
              repeat: -1,
            });

            // 3) 종료: duration 뒤 깜빡임 중지 + 서서히 사라짐
            this.topWarningTimer = this.scene.time.delayedCall(duration, () => {
              if (blinkTween) {
                blinkTween.stop();
              }

              text.setAlpha(1);
              text.setX(baseX);

              this.scene.tweens.add({
                targets: text,
                alpha: 0,
                duration: 500,
                ease: "Cubic.Out",
                onComplete: () => {
                  text.destroy();
                },
              });

              this.topWarningTimer = undefined;
            });
          },
        });
      },
    });
  }

  // 효과 타이머 배열 반환
  private getEffectTimers() {
    return [
      this.topWarningTimer,
      this.seaObstacleEndTimer,
      this.seaObstacleDestroyTimer,
      this.desertSmokeTimer,
      this.desertObstacleEndTimer,
      this.forestObstacleEndTimer,
      this.factoryObstacleEndTimer,
    ];
  }

  // 게임 일시정지 시 이펙트도 멈추도록
  pauseEffects() {
    this.seaObstacleParticles?.pause();

    this.getEffectTimers().forEach((timer) => {
      if (timer) timer.paused = true;
    });
  }

  // 게임 재개 시 이펙트도 다시 재생
  resumeEffects() {
    this.seaObstacleParticles?.resume();

    this.getEffectTimers().forEach((timer) => {
      if (timer) timer.paused = false;
    });
  }
}
